import moment from 'moment';
import { sortBy, get } from 'lodash-es';

function getTimeFromMinutes(mins: number, format = 'hh:mm A') {
  if (!mins && mins !== 0) return '';

  const h = mins / 60 | 0; const m = mins % 60 | 0;
  return moment.utc().hours(h).minutes(m).format(format);
}

function getDelayTimeFromMinutes(mins: number) {
  if (!mins) {
    return '';
  }

  if (mins < 60) {
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    return String(mins + ' mins');
  }

  // tslint:disable-next-line: no-bitwise
  const h = mins / 60 | 0; const m = mins % 60 | 0;
  return moment.utc().hours(h).minutes(m).format('hh:mm') + ' hour';
}

function getReadableDate(date: moment.MomentInput) {
  const onlyDate = moment().format('YYYY-MM-DD');
  const todayDate = moment(onlyDate, 'YYYY-MM-DD');
  const mydate = moment(date, 'YYYY-MM-DD');
  const diffInDays = mydate.diff(todayDate, 'days');
  let availableDate = mydate.format('ddd, DD MMM');
  // tslint:disable-next-line: triple-equals
  if (diffInDays == 0) {
    availableDate = 'Today, ' + mydate.format('DD MMM');
  }
  // tslint:disable-next-line: triple-equals
  if (diffInDays == 1) {
    availableDate = 'Tomorrow, ' + mydate.format('DD MMM');
  }

  return availableDate;

}

function getAgeFromDOB(date: moment.MomentInput) {
  const dob = moment(date, 'YYYY-MM-DD HH:mm:ss ZZ'),
    now = moment(),
    y = now.diff(dob, 'year'),
    m = now.diff(dob.add(y, 'years'), 'months'),
    d = now.diff(dob.add(m, 'months'), 'days');
  return [y && `${y}y`, m && `${m}m`, d && `${d}d`].filter(Boolean).join(' ');
}

function getNextPathname({ type }: any = {}) {
  let pathname = '/dashboard';
  switch (type) {
    case 'view_prescription':
      pathname = '/records';
      break;
    case 'appointment_cancelled':
      pathname = '/appointment';
      break;
    case 'chat':
      pathname = '/chat';
      break;
    case 'cross_offering_order':
      pathname = '/cart';
      break;
  }

  return pathname;
}

function processVisitData(data: string | any[]) {
  // tslint:disable-next-line: prefer-for-of
  const yearBuckets = {};
  const years: any = [];
  const visits: any = [];
  for (let i = 0; i < data.length; i++) {
    const obj = Object.assign({}, data[i].visit);
    const dateTimeISOMoment = moment(obj['dateTimeISO'], 'YYYY-MM-DD HH:mm:ss ZZ');
    const year = dateTimeISOMoment.year();
    if (!(yearBuckets[year])) {
      yearBuckets[year] = [];
      years.push(year);
    }

    obj.date = dateTimeISOMoment.format('DD');
    obj.month = dateTimeISOMoment.format('MMM');
    switch (obj?.appointment?.reason) {
      case 'followup':
        obj.visitType = 'Follow-up'; break;
      case 'vaccination':
        obj.visitType = 'Vaccination'; break;
      case 'consultation':
        obj.visitType = 'Consultation'; break;
      case undefined: break;
      default:
        obj.visitType = obj.appointment.reason; break;
    }

    obj.doctorFullName = '';
    obj.patientFullName = `${obj.patient.firstName || ''} ${obj.patient.lastName || ''}`;
    if (obj?.doctorDetails?.firstName) {
      obj.doctorFullName = `Dr. ${obj.doctorDetails.firstName} ${obj.doctorDetails.lastName || ''}`;
    }

    yearBuckets[year].push(obj);
    visits.push(obj);
  }

  // setVisitByYear(yearBuckets);
  // setYears(sortBy(years).reverse());
  // setIsFetching(false);

  return { visitByYear: yearBuckets, years: sortBy(years).reverse(), visits };
}

function processAppointments(data: string | any[]) {
  // tslint:disable-next-line: prefer-for-of
  const yearBuckets = {};
  const years: any = [];
  const appointments: any = [];

  for (let i = 0; i < data.length; i++) {
    const obj = Object.assign({}, data[i]);
    const dateTimeISOMoment = moment(obj['appointmentDate'], 'YYYY-MM-DD HH:mm:ss ZZ');
    const year = dateTimeISOMoment.year();
    if (!(yearBuckets[year])) {
      yearBuckets[year] = [];
      years.push(year);
    }

    obj.date = dateTimeISOMoment.format('DD');
    obj.month = dateTimeISOMoment.format('MMM');

    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    obj.parseAppointmentTime = obj.type === 'walkin' ? 'Walkin' : getTimeFromMinutes(obj.appointmentTime + (obj.delay || 0));

    obj.refrence = 'myappointments';

    obj.doctorFullName = '';
    if (obj?.doctorDetails?.firstName) {
      obj.doctorFullName = `Dr. ${obj.doctorDetails.firstName} ${obj.doctorDetails.lastName || ''}`;
    }

    yearBuckets[year].push(obj);
    appointments.push(obj);
  }

  return { appointmentsByYear: yearBuckets, years: sortBy(years).reverse(), appointments };
}

function getAddressString(adr, msg = false) {
  const {
    line1,
    line2,
    area,
    pincode,
    city,
    state,
  } = adr || {};
  const address: string[] = [];
  if (line1) { address.push(line1); }
  if (line2) { address.push(line2); }
  if (area) { address.push(area); }
  if (pincode && !msg) { address.push(pincode); }
  if (city && !msg) { address.push(city); }
  if (state && !msg) { address.push(state); }

  return address.join(', ');
}

// Fiter remaining slots for next available shift
function filterLeftSlots({
  shift = {},
  date, // nextSlot date
  doctor,
  includeWalkin = false,
}: { shift: any; date: string; doctor: any, includeWalkin: boolean; }) {
  const mmt = moment();
  const todaysDateStr = mmt.format('YYYY-MM-DD');
  const nextSlotDate = moment(date).format('YYYY-MM-DD');
  const diffMinutes = getDenyBufferMinutes(doctor);

  const filteredSlots: any = [];
  shift.slots.forEach(slot => {
    if (includeWalkin ? (!slot.type || slot.type === 'walkin') : !slot.type) {
      if (todaysDateStr !== nextSlotDate || (todaysDateStr == nextSlotDate && (slot.startTime > diffMinutes))) {
        filteredSlots.push({
          ...slot,
          parsedStartTime: getTimeFromMinutes(slot.startTime),
          teleConsult: shift.teleConsult,
          shiftID: shift.shiftID,
        });
      }
    }
  });

  return filteredSlots;
}

function getDenyBufferMinutes(doctor) {
  const mmt = moment();
  const mmtMidnight = mmt.clone().startOf('day');
  const diffMinutes = mmt.diff(mmtMidnight, 'minutes'); // diff in minutes from current timepicker
  const denyAppointmentAfterMinutes: number = get(doctor, 'preferences.denyAppointmentAfterMinutes');
  if (denyAppointmentAfterMinutes) {
    return diffMinutes + denyAppointmentAfterMinutes;
  }
  return diffMinutes;
}

function processNextAvailableSlots({
  shifts = [],
  date,
  doctor,
  onlyCheckBookingEnable = false,
  includeWalkin = false,
}: any) {
  let totalSlots = 0;
  let isBookingAllowed = false;
  const slots: any = [];
  shifts.forEach(shift => {
    const startDate = moment(shift.validFrom, 'YYYY-MM-DD HH:mm:ss ZZ');
    const endDate = moment(shift.validTill, 'YYYY-MM-DD HH:mm:ss ZZ');

    if (
      shift.status !== 'deleted' &&
      shift.shiftTypes.length > 0 &&
      (includeWalkin ? true : shift.shiftTypes[0] !== 'walkin') &&
      // because validity of dynamic slot is 1Day
      (shift.volatility == 'dynamic' ? true : moment().isBetween(startDate, endDate)) &&
      shift?.visibility === 'on'
    ) {

      isBookingAllowed = true;
      if (onlyCheckBookingEnable && isBookingAllowed) return;
      if (onlyCheckBookingEnable) return;

      const filteredSlots = filterLeftSlots({ shift, date, doctor, includeWalkin });
      totalSlots += filteredSlots?.length;
      if (filteredSlots?.length > 0) slots.push(filteredSlots);
    }
  });

  if (onlyCheckBookingEnable) return isBookingAllowed;

  return { slots, totalSlots };
}

function formatMobileNumber(num: string | number) {
  return '+91' + `${num}`.slice(-10);
}

function checkBookingEnable({ shifts = [] }: any = {}) {
  return processNextAvailableSlots({ shifts, onlyCheckBookingEnable: true });
}

function getShiftType(shift: {
  shiftID?: any; doctorID?: any; clinicID?: any; teleConsult?: any; shiftTypes?: any;
}) {
  if (shift?.shiftTypes.indexOf('appointment') > -1)
    return 'appointment';

  if (shift?.shiftTypes.indexOf('token') > -1)
    return 'token';
  return 'walkin';
}

export {
  getTimeFromMinutes,
  getDelayTimeFromMinutes,
  getNextPathname,
  getReadableDate,
  getAgeFromDOB,
  processVisitData,
  processAppointments,
  getAddressString,
  processNextAvailableSlots,
  formatMobileNumber,
  getDenyBufferMinutes,
  filterLeftSlots,
  checkBookingEnable,
  getShiftType,
};